import { Avatar, Badge, Dialog, DialogContent, DialogContentText, IconButton, makeStyles, DialogTitle as MuiDialogTitle, SvgIcon, Theme, Typography } from "@material-ui/core";
import { blue, deepOrange, green, grey, orange, red, teal } from "@material-ui/core/colors";
import NewMessageIcon from '@material-ui/icons/AddCommentOutlined';
import MessageIcon from '@material-ui/icons/ChatBubbleOutline';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import CloseIcon from "@material-ui/icons/Close";
import { default as DescriptionOutlinedIcon, default as InvoiceIcon } from '@material-ui/icons/DescriptionOutlined';
import FactDateIcon from '@material-ui/icons/EventAvailableOutlined';
import PlanDateIcon from '@material-ui/icons/EventOutlined';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LocationDividerIcon from '@material-ui/icons/ForwardOutlined';
import CargoIcon from '@material-ui/icons/LocalMallOutlined';
import DeliveryIcon from '@material-ui/icons/LocalShippingOutlined';
import MyMessageIcon from '@material-ui/icons/ModeComment';
import NotificationsActiveOutlinedIcon from '@material-ui/icons/NotificationsActiveOutlined';
import PaymentIcon from '@material-ui/icons/PaymentOutlined';
import PartnerIcon from '@material-ui/icons/PersonPinCircleOutlined';
import PdfIcon from '@material-ui/icons/PictureAsPdfOutlined';
import MessagesIcon from '@material-ui/icons/QuestionAnswerOutlined';
import LocationIcon from '@material-ui/icons/RoomOutlined';
import EventIcon from '@material-ui/icons/ScheduleOutlined';
import SearchIcon from '@material-ui/icons/Search';
import CancelNewMessageIcon from '@material-ui/icons/SpeakerNotesOffOutlined';
import SendChatMessageIcon from '@material-ui/icons/Telegram';
import TextsmsOutlinedIcon from '@material-ui/icons/TextsmsOutlined';
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

interface HelpDialogProps
{
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    maxHeight: '60vh',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  iconSection: {
    marginBottom: theme.spacing(3),
  },
  iconRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  iconLabel: {
    marginLeft: theme.spacing(1),
  },
  statusAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    marginRight: 2,
    borderWidth: 0,
  },
  completedAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: green[600],
    color: theme.palette.getContrastText(green[600]),
    marginRight: 2,
    borderWidth: 0,
  },
  inProgressAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: blue[600],
    color: theme.palette.getContrastText(blue[600]),
    marginRight: 2,
    borderWidth: 0,
  },
  errorAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: red[600],
    color: theme.palette.getContrastText(red[600]),
    marginRight: 2,
    borderWidth: 0,
  },
  eventAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    color: green[600],
    marginRight: 2,
    borderWidth: 0,
    fontSize: 20,
  },
  pdfAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.getContrastText(theme.palette.background.paper),
    marginRight: 2,
    borderWidth: 0,
    fontSize: 20,
    border: 0
  },
  messagesAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    color: blue[600],
    marginRight: 2,
    borderWidth: 0,
    fontSize: 20,
  },
  statusIcon: {
    fontSize: 16,
  },
  orderAvatar: {
    backgroundColor: grey[600],
    color: theme.palette.getContrastText(grey[600]),
  },
  orderAvatarInProgress: {
    backgroundColor: blue[600],
    color: theme.palette.getContrastText(blue[600]),
  },
  orderAvatarCompleted: {
    backgroundColor: green[600],
    color: theme.palette.getContrastText(green[600]),
  },
  orderAvatarError: {
    backgroundColor: red[600],
    color: theme.palette.getContrastText(red[600]),
  },
  newOrderBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: orange[600],
      color: theme.palette.getContrastText(orange[600]),
    },
  },
  selectedOrderBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: red[700],
      color: theme.palette.getContrastText(red[700]),
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      width: 24,
      height: 24,
      minWidth: 24,
      minHeight: 24,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 18,
      padding: 0,
    },
  },
  newOrderEventBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: orange[600],
      color: theme.palette.getContrastText(orange[600]),
    },
  },
  selectedOrderEventBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: red[700],
      color: theme.palette.getContrastText(red[700]),
    },
  },
  partnerIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: teal[600],
  },
  locationIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: deepOrange[600],
  },
  cargoIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: blue[600],
  },
  planDateIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: grey[400],
  },
  factDateIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: green[600],
  },
  locationDividerIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: grey[400],
    transform: "rotate(90deg)",
  },
  locationProgressDividerIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: blue[600],
    transform: "rotate(90deg)",
  },
  locationCompletedDividerIcon: {
    fontSize: 20,
    marginBottom: -5,
    color: green[600],
    transform: "rotate(90deg)",
  },
  eventIcon: {
    fontSize: 20,
    marginBottom: -5,
    marginRight: 4,
    color: green[600],
  },
  pdfIcon: {
    fontSize: 20,
    marginBottom: -5,
  },
  chatMessageIcon: {
    fontSize: 20,
    marginBottom: -5,
    marginRight: 4,
    color: blue[600],
  },
  chatMessagesButton: {
    '& .MuiBadge-badge': {
      backgroundColor: blue[600],
      color: theme.palette.getContrastText(blue[600]),
    },
    '&.unread .MuiBadge-badge': {
      backgroundColor: orange[600],
      color: theme.palette.getContrastText(orange[600]),
    },
  },
  barcodeIcon: {
    width: 26,
    height: 26,
  },
  subtitleFirst: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  subtitleWithTopMargin: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    fontWeight: 600,
  },
  notificationIcon: {
    width: 18,
    height: 18,
  },
}));

const HelpDialog: FunctionComponent<HelpDialogProps> = ({ open, onClose }) =>
{
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <MuiDialogTitle disableTypography>
        <Typography variant="h6">{t('partnerPortal.help.title')}</Typography>
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </MuiDialogTitle>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText component="div">

          <div className={classes.iconSection}>
            <Typography variant="h6" gutterBottom>{t('partnerPortal.help.content.orderSection.title')}</Typography>

            <div className={classes.iconRow}>
              <Avatar className={classes.orderAvatar}>
                <DescriptionOutlinedIcon />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderSection.registered')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Avatar className={classes.orderAvatarInProgress}>
                <DescriptionOutlinedIcon />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderSection.inProgress')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Avatar className={classes.orderAvatarCompleted}>
                <DescriptionOutlinedIcon />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderSection.completed')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Avatar className={classes.orderAvatarError}>
                <DescriptionOutlinedIcon />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderSection.paymentDelay')}</Typography>
            </div>
          </div>

          <div className={classes.iconRow}>
            <Badge className={classes.newOrderBadge} badgeContent={3} variant="dot">
              <Avatar className={classes.orderAvatar}>
                <DescriptionOutlinedIcon />
              </Avatar>
            </Badge>
            <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderSection.newOrder')}</Typography>
          </div>

          <div className={classes.iconRow}>
            <Badge className={`${classes.newOrderBadge} ${classes.selectedOrderBadge}`} badgeContent={<NotificationsActiveOutlinedIcon fontSize="small" className={classes.notificationIcon} />}>
              <Avatar className={classes.orderAvatarInProgress}>
                <DescriptionOutlinedIcon />
              </Avatar>
            </Badge>
            <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderSection.notificationOrder')}</Typography>
          </div>

          <div className={classes.iconSection}>
            <Typography variant="h6" gutterBottom>{t('partnerPortal.help.content.statusSection.title')}</Typography>

            <Typography variant="subtitle2" className={classes.subtitleFirst}>{t('partnerPortal.help.content.statusSection.delivery.title')}</Typography>
            <div className={classes.iconRow}>
              <Avatar className={classes.statusAvatar}>
                <DeliveryIcon className={classes.statusIcon} />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.delivery.notStarted')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Avatar className={classes.inProgressAvatar}>
                <DeliveryIcon className={classes.statusIcon} />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.delivery.inProgress')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Avatar className={classes.completedAvatar}>
                <DeliveryIcon className={classes.statusIcon} />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.delivery.completed')}</Typography>
            </div>

            <Typography variant="subtitle2" className={classes.subtitleWithTopMargin}>{t('partnerPortal.help.content.statusSection.invoicing.title')}</Typography>
            <div className={classes.iconRow}>
              <Avatar className={classes.statusAvatar}>
                <InvoiceIcon className={classes.statusIcon} />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.invoicing.notInvoiceable')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Avatar className={classes.inProgressAvatar}>
                <InvoiceIcon className={classes.statusIcon} />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.invoicing.inProgress')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Avatar className={classes.completedAvatar}>
                <InvoiceIcon className={classes.statusIcon} />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.invoicing.invoiced')}</Typography>
            </div>

            <Typography variant="subtitle2" className={classes.subtitleWithTopMargin}>{t('partnerPortal.help.content.statusSection.payment.title')}</Typography>
            <div className={classes.iconRow}>
              <Avatar className={classes.statusAvatar}>
                <PaymentIcon className={classes.statusIcon} />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.payment.notInvoiced')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Avatar className={classes.inProgressAvatar}>
                <PaymentIcon className={classes.statusIcon} />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.payment.payable')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Avatar className={classes.completedAvatar}>
                <PaymentIcon className={classes.statusIcon} />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.payment.successful')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Avatar className={classes.errorAvatar}>
                <PaymentIcon className={classes.statusIcon} />
              </Avatar>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.payment.overdue')}</Typography>
            </div>

            <Typography variant="subtitle2" className={classes.subtitleWithTopMargin}>{t('partnerPortal.help.content.statusSection.other.title')}</Typography>
            <div className={classes.iconRow}>
              <Badge className={classes.newOrderBadge} badgeContent={3}>
                <Avatar className={classes.eventAvatar} variant="circular">
                  <EventIcon fontSize="inherit" />
                </Avatar>
              </Badge>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.other.events')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Badge className={classes.newOrderBadge} badgeContent={2} color="secondary">
                <Avatar className={classes.pdfAvatar} variant="circular">
                  <PdfIcon fontSize="inherit" />
                </Avatar>
              </Badge>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.other.pdfs')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Badge className={classes.newOrderBadge} badgeContent={5} color="secondary">
                <Avatar className={classes.messagesAvatar} variant="circular">
                  <MessagesIcon fontSize="inherit" />
                </Avatar>
              </Badge>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.statusSection.other.messages')}</Typography>
            </div>
          </div>

          <div className={classes.iconSection}>
            <Typography variant="h6" gutterBottom>{t('partnerPortal.help.content.orderDataSection.title')}</Typography>

            <Typography variant="subtitle2" className={classes.subtitleFirst}>{t('partnerPortal.help.content.orderDataSection.cargo.title')}</Typography>
            <div className={classes.iconRow}>
              <CargoIcon className={classes.cargoIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.cargo.label')}</Typography>
            </div>

            <Typography variant="subtitle2" className={classes.subtitleWithTopMargin}>{t('partnerPortal.help.content.orderDataSection.locations.title')}</Typography>
            <div className={classes.iconRow}>
              <PartnerIcon className={classes.partnerIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.locations.partner')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <LocationIcon className={classes.locationIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.locations.address')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <LocationDividerIcon className={classes.locationDividerIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.locations.sectionNotStarted')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <LocationDividerIcon className={classes.locationProgressDividerIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.locations.sectionInProgress')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <LocationDividerIcon className={classes.locationCompletedDividerIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.locations.sectionCompleted')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <PlanDateIcon className={classes.planDateIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.locations.plannedDate')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <FactDateIcon className={classes.factDateIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.locations.factDate')}</Typography>
            </div>

            <Typography variant="subtitle2" className={classes.subtitleWithTopMargin}>{t('partnerPortal.help.content.orderDataSection.events.title')}</Typography>
            <div className={classes.iconRow}>
              <EventIcon className={classes.eventIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.events.timestamp')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Badge className={classes.newOrderEventBadge} badgeContent=" " variant="dot" overlap="circular">
                <EventIcon className={classes.eventIcon} />
              </Badge>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.events.new')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Badge className={classes.selectedOrderEventBadge} badgeContent=" " variant="dot" overlap="circular">
                <EventIcon className={classes.eventIcon} />
              </Badge>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.events.notification')}</Typography>
            </div>

            <Typography variant="subtitle2" className={classes.subtitleWithTopMargin}>{t('partnerPortal.help.content.orderDataSection.pdf.title')}</Typography>
            <div className={classes.iconRow}>
              <PdfIcon className={classes.pdfIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.pdf.document')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Badge className={classes.newOrderBadge} badgeContent=" " variant="dot" overlap="circular">
                <PdfIcon className={classes.pdfIcon} />
              </Badge>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.orderDataSection.pdf.new')}</Typography>
            </div>
          </div>

          <div className={classes.iconSection}>
            <Typography variant="h6" gutterBottom>{t('partnerPortal.help.content.messagesSection.title')}</Typography>

            <div className={classes.iconRow}>
              <MessageIcon className={classes.chatMessageIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.messagesSection.received')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <MyMessageIcon className={classes.chatMessageIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.messagesSection.own')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Badge className={classes.newOrderBadge} badgeContent=" " variant="dot" overlap="circular">
                <MessageIcon className={classes.chatMessageIcon} />
              </Badge>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.messagesSection.unread')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Badge className={`${classes.chatMessagesButton}`} badgeContent={5}>
                <MessagesIcon fontSize="inherit" color="primary" />
              </Badge>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.messagesSection.buttonRead')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <Badge className={`${classes.chatMessagesButton} unread`} badgeContent={3}>
                <MessagesIcon fontSize="inherit" color="primary" />
              </Badge>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.messagesSection.buttonUnread')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <NewMessageIcon fontSize="inherit" />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.messagesSection.newMessage')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <CancelNewMessageIcon fontSize="inherit" />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.messagesSection.cancelNewMessage')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <SendChatMessageIcon fontSize="medium" className={classes.chatMessageIcon} />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.messagesSection.sendMessage')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <TextsmsOutlinedIcon />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.messagesSection.loadMore')}</Typography>
            </div>
          </div>

          <div className={classes.iconSection}>
            <Typography variant="h6" gutterBottom>{t('partnerPortal.help.content.navigationSection.title')}</Typography>

            <div className={classes.iconRow}>
              <ExpandMoreIcon fontSize="inherit" />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.navigationSection.expand')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <ExpandLessIcon fontSize="inherit" />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.navigationSection.collapse')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <SearchIcon />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.navigationSection.search')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <ClearOutlinedIcon fontSize="inherit" />
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.navigationSection.clearSearch')}</Typography>
            </div>

            <div className={classes.iconRow}>
              <SvgIcon className={classes.barcodeIcon}>
                <path stroke="null" id="svg_3" d="m1.046054,4.697369c0,-0.576125 0.467108,-1.043233 1.043233,-1.043233l2.608082,0l0,-1.043233l-2.608082,0c-1.152251,0 -2.086466,0.934215 -2.086466,2.086466l0,2.608082l1.043233,0l0,-2.608082zm0,14.605261l0,-2.608082l-1.043233,0l0,2.608082c0,1.152251 0.934215,2.086466 2.086466,2.086466l2.608082,0l0,-1.043233l-2.608082,0c-0.576125,0 -1.043233,-0.467108 -1.043233,-1.043233zm21.907892,0c0,0.576125 -0.467108,1.043233 1.043233,1.043233l-2.608082,0l0,1.043233l2.608082,0c1.152251,0 2.086466,-0.934215 2.086466,-2.086466l0,-2.608082l-1.043233,0l0,2.608082zm-1.043233,-16.691727l-2.608082,0l0,1.043233l2.608082,0c0.576125,0 1.043233,0.467108 1.043233,1.043233l0,2.608082l1.043233,0l0,-2.608082c0,-1.152251 -0.934215,-2.086466 -2.086466,-2.086466zm-20.864659,9.389096c0,0.287932 0.233684,0.521616 0.521616,0.521616l20.864659,0c0.287932,0 0.521616,-0.233684 0.521616,-0.521616s-0.233684,-0.521616 -0.521616,-0.521616l-20.864659,0c-0.287932,0 -0.521616,0.233684 -0.521616,0.521616zm8.345864,-6.259398l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm-6.259398,0l0,4.694548l1.043233,0l0,-4.694548l-1.043233,0zm8.345864,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm5.216165,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm2.086466,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm1.043233,0l0,4.694548l1.043233,0l0,-4.694548l-1.043233,0zm-12.518795,0l-2.086466,0l0,4.694548l2.086466,0l0,-4.694548zm7.302631,0l-2.086466,0l0,4.694548l2.086466,0l0,-4.694548zm-6.259398,12.518795l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm-4.172932,0l0,-4.694548l-1.043233,0l0,4.694548l1.043233,0zm6.259398,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm5.216165,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm2.086466,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm3.129699,0l0,-4.694548l-1.043233,0l0,4.694548l1.043233,0zm-15.648494,0l2.086466,0l0,-4.694548l-2.086466,0l0,4.694548zm7.302631,0l2.086466,0l0,-4.694548l-2.086466,0l0,4.694548z" clip-rule="evenodd" fill-rule="evenodd" />
              </SvgIcon>
              <Typography className={classes.iconLabel}>{t('partnerPortal.help.content.navigationSection.barcode')}</Typography>
            </div>
          </div>

        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default HelpDialog;
